import type React from 'react';
import { useSelector } from 'react-redux';

// Constellation
import { Button, Text, TextButton } from '@zillow/constellation';

// Components
import LazyLoading from 'app/shared/components/LazyLoad';
import ResponsiveHeading from 'app/shared/StyledComponents/ResponsiveText';

// Styles
import * as S from './styles';

// Types
import type { State } from 'app/shared/types/State';

// Utils
import optimizelyUtils from 'app/shared/utils/analytics/optimizelyUtils';

type SlideProps = {
  body: string | React.ReactElement;
  btnPrimaryHref: string;
  btnPrimaryLabel: string;
  btnSecondaryHref?: string;
  btnSecondaryLabel?: string;
  heading: string;
  imgPosition: 'left' | 'right';
  srcBackground: string;
  srcOverlay?: string;
};

const Slide = ({
  body,
  btnPrimaryHref,
  btnPrimaryLabel,
  btnSecondaryHref,
  btnSecondaryLabel,
  heading,
  imgPosition,
  srcBackground,
  srcOverlay,
}: SlideProps) => {
  const device = useSelector((state: State) => state.app.device);
  const isMobileOrTablet = device.isMobile || device.screenWidth === 'md' || device.screenWidth === 'lg';

  const trackBtnPrimaryClick = () => {
    optimizelyUtils.track();
  };

  const renderImg = () =>
    isMobileOrTablet ? (
      <S.ImageContainerMweb usePadding={!srcOverlay}>
        {srcOverlay ? (
          <LazyLoading>
            <S.OverlayImage src={srcOverlay} style={{ height: '100%' }} />
          </LazyLoading>
        ) : (
          <LazyLoading>
            <img
              src={srcBackground}
              style={{
                boxShadow: 'rgba(130, 130, 130, 0.8) 0px 0px 20px',
                maxHeight: '100%',
              }}
            />
          </LazyLoading>
        )}
      </S.ImageContainerMweb>
    ) : (
      <S.ImageContainer imgPosition={imgPosition}>
        <LazyLoading>
          <S.Background src={srcBackground} />
        </LazyLoading>
        {srcOverlay && (
          <S.OverlayContainer imgPosition={imgPosition}>
            <LazyLoading>
              <S.OverlayImage src={srcOverlay} />
            </LazyLoading>
          </S.OverlayContainer>
        )}
      </S.ImageContainer>
    );

  const renderText = () => (
    <S.TextContainer>
      <ResponsiveHeading level="3" fontType="h3" marginBottom="sm">
        {heading}
      </ResponsiveHeading>
      <Text as="div" fontType="body" marginBottom="md">
        {body}
      </Text>
      <Button as="a" buttonType="primary" href={btnPrimaryHref} onClick={trackBtnPrimaryClick} size="sm">
        {btnPrimaryLabel}
      </Button>
      {btnSecondaryLabel && (
        <TextButton as="a" marginLeft="md" href={btnSecondaryHref}>
          {btnSecondaryLabel}
        </TextButton>
      )}
    </S.TextContainer>
  );
  return (
    <S.Container>
      {imgPosition === 'left' || isMobileOrTablet ? (
        <>
          {renderImg()}
          {renderText()}
        </>
      ) : (
        <>
          {renderText()}
          {renderImg()}
        </>
      )}
    </S.Container>
  );
};

export default Slide;
